// Section 4 phase 8 and Section 19: events (stream E2, ADR-0016). In order:
//   1. upkeep: expired effects dropped, per-season effect stock, blighted seed, old warnings;
//   2. guests whose stay is over are decided on again (Section 14);
//   3. the deck: this season's draw (made last season, or now on the first season) and the next
//      season's draw, whose warnings go into this report (foreshadowing);
//   4. this season's events fire: each adds its headline entry and applies its immediate deltas
//      (effects from next season, people falling ill, a building on fire, a prompt, arrivals);
//   5. groups that arrived are asked about or decided by the migration policy.
// Event draws consume only `ctx.eventRng`, a fixed count per season; an event's own randomness is
// a sub-stream seeded from its draw, so decisions change effects but never the draws (24.13).
import type { EventDraw, Phase, PhaseContext } from '../../core/types';
import { drawDeck } from './deck';
import { firedEvents, runEvent } from './execute';
import { foreshadow } from './foreshadow';
import { gateNewGroups, guestsDecideAgain, pruneGroups } from './gate';
import { upkeep } from './upkeep';

function drawFor(ctx: PhaseContext, turn: number, warn: boolean): EventDraw {
  const existing = ctx.draft.eventDraws.find((x) => x.turn === turn);
  if (existing !== undefined) return existing;
  const draw = drawDeck(ctx, turn);
  ctx.draft.eventDraws.push(draw);
  if (warn) foreshadow(ctx, draw);
  return draw;
}

export const eventsPhase: Phase = {
  id: 'events',
  reads: [
    'activeEffects',
    'foreshadows',
    'eventDraws',
    'options',
    'eventRngState',
    'people',
    'households',
    'buildings',
    'stocks',
    'policies',
    'norms',
    'arrivalGroups',
    'counters',
    'currentReport',
  ],
  writes: [
    'activeEffects',
    'foreshadows',
    'eventDraws',
    'openPrompts',
    'eventRngState',
    'currentReport',
    'people',
    'households',
    'buildings',
    'stocks',
    'seedReserved',
    'arrivalGroups',
    'nextIds',
    'counters',
    'quarantine',
    'factionMarks',
    'formerPeople',
  ],
  projection: 'skip',
  run(ctx) {
    const turn = ctx.season.turn;
    // Saves made before the deck existed have no draws yet (ADR-0016).
    if ((ctx.draft.eventDraws as EventDraw[] | undefined) === undefined) ctx.draft.eventDraws = [];
    upkeep(ctx);
    guestsDecideAgain(ctx);
    const current = drawFor(ctx, turn, false);
    drawFor(ctx, turn + 1, true);
    const fired = firedEvents(ctx, current);
    for (const c of fired) runEvent(ctx, c, turn);
    gateNewGroups(ctx);
    pruneGroups(ctx, ctx.cfg.migration.groupRecordSeasons);
    ctx.draft.eventDraws = ctx.draft.eventDraws.filter((x) => x.turn > turn);
    return 'done';
  },
};
